import { createSlice } from '@reduxjs/toolkit'
import { logoutUser } from '../middlewares/logoutMiddleware'
import { rubricRefinementApi } from '../apis/rubric-refinement.api'
import { RefinementItemTypes } from '../../pages/assignment/components/section-menu/rubric-refinement/chat/constants'

/**
 * Initial state for the rubric refinement slice
 * refinementItems: Array of standardized RefinementItem objects
 * refinementProcess: Process metadata from the API
 * hoveredCategoryId: Currently hovered category ID for UI
 * hasStarted: Whether user clicked 'Let's get started' button
 * processingAction: Whether an action is currently being processed
 * isFinished: Whether the refinement process is complete (item_to_review was null)
 */
const initialState = {
    refinementItems: [],
    refinementProcess: null,
    hoveredCategoryId: null,
    hasStarted: false,
    processingAction: false, // Track if we're currently processing an action
    isFinished: false, // Track if the refinement process is complete
}

const rubricRefinementSlice = createSlice({
    name: 'rubricRefinement',
    initialState,
    reducers: {
        setRefinementItems: (state, action) => {
            state.refinementItems = action.payload
        },
        setHoveredCategoryId: (state, action) => {
            state.hoveredCategoryId = action.payload
        },
        selectCategory: (state, action) => {
            const { refinementItemId, categoryId } = action.payload
            const currentItem = state.refinementItems.find(item => item.id === refinementItemId)
            if (currentItem) {
                currentItem.selectedCategoryId = categoryId
                currentItem.isSkipped = false
                state.processingAction = true // Start processing
            }
        },
        approveDiffReview: (state, action) => {
            const { refinementItemId } = action.payload
            const currentItem = state.refinementItems.find(item => item.id === refinementItemId)
            if (currentItem) {
                currentItem.diffReviewChoice = 'approved'
                state.processingAction = true // Start processing
            }
        },
        rejectDiffReview: (state, action) => {
            const { refinementItemId } = action.payload
            const currentItem = state.refinementItems.find(item => item.id === refinementItemId)
            if (currentItem) {
                currentItem.diffReviewChoice = 'rejected'
                state.processingAction = true // Start processing
            }
        },
        skipItem: (state, action) => {
            const { refinementItemId } = action.payload
            const currentItem = state.refinementItems.find(item => item.id === refinementItemId)
            if (currentItem) {
                currentItem.isSkipped = true
                currentItem.selectedCategoryId = null
                state.processingAction = true // Start processing
                if (currentItem.type === RefinementItemTypes.SUGGESTION) {
                    currentItem.diffReviewChoice = 'rejected'
                }
            }
        },
        revertItemToState: (state, action) => {
            const { refinementItemId, previousState } = action.payload
            const currentItem = state.refinementItems.find(item => item.id === refinementItemId)
            if (currentItem) {
                currentItem.selectedCategoryId = previousState.selectedCategoryId
                currentItem.isSkipped = previousState.isSkipped
                if (previousState.diffReviewChoice !== undefined) {
                    currentItem.diffReviewChoice = previousState.diffReviewChoice
                }
            }
        },
        startRefinementProcess: state => {
            state.hasStarted = true
        },
        reset: () => initialState,
    },
    extraReducers: builder => {
        builder
            .addCase(logoutUser, () => initialState)
            // When a new refinement starts, reset the list to contain only the new item
            .addMatcher(rubricRefinementApi.endpoints.startRefinement.matchFulfilled, (state, { payload }) => {
                if (payload) {
                    state.refinementProcess = payload
                    state.isFinished = false

                    // Initialize refinementItems with reviewedItems if available
                    let initialItems = []

                    // Add reviewed items if they exist
                    if (payload.reviewedItems && payload.reviewedItems.length > 0) {
                        // Process the reviewed items to create new objects with the correct properties
                        initialItems = payload.reviewedItems.map(item => {
                            // Create a new object instead of modifying the existing one
                            const processedItem = { ...item }

                            if (item.type === RefinementItemTypes.SUBMISSION) {
                                if (item.userDecision === 'Skip') {
                                    processedItem.isSkipped = true
                                } else {
                                    processedItem.selectedCategoryId = +item.userDecision
                                }
                            } else if (item.type === RefinementItemTypes.SUGGESTION) {
                                // For suggestions, set the diffReviewChoice based on user decision
                                if (item.userDecision === 'ChangeAccepted') {
                                    processedItem.diffReviewChoice = 'approved'
                                } else {
                                    processedItem.diffReviewChoice = 'rejected'
                                }
                            }
                            return processedItem
                        })
                    }

                    if (initialItems.length) state.hasStarted = true
                    if (payload.item) initialItems = [...initialItems, payload.item]

                    state.refinementItems = initialItems
                }
            })
            // When choosing a category, add the returned item to the list
            .addMatcher(rubricRefinementApi.endpoints.chooseRefinementCategory.matchFulfilled, (state, action) => {
                state.processingAction = false // End processing

                // Replace the item ID if a new reviewed item ID is provided
                if (action.payload?.newReviewedItemId) {
                    const currentItem = state.refinementItems.find(
                        item => item.id === action.meta.arg.originalArgs.refinementItemId,
                    )
                    if (currentItem) {
                        currentItem.id = action.payload.newReviewedItemId
                    }
                }

                // If we get a null item_to_review, the process is finished
                if (action.payload?.item === null) {
                    state.isFinished = true
                    return
                }

                if (action.payload?.item) {
                    // Set the selected category ID on the current item
                    const currentItem = state.refinementItems.find(
                        item => item.id === (action.payload.newReviewedItemId || action.meta.arg.originalArgs.refinementItemId),
                    )
                    if (currentItem && currentItem.type === RefinementItemTypes.SUBMISSION) {
                        currentItem.selectedCategoryId = action.meta.arg.originalArgs.categoryId
                    }

                    // Add the new item to the list if it's not already present
                    const newItem = action.payload.item
                    if (!state.refinementItems.some(item => item.id === newItem.id)) {
                        state.refinementItems.push(newItem)
                    }

                    // Update the progress
                    if (action.payload.progress) {
                        state.refinementProcess.progress = action.payload.progress
                    }
                }
            })
            // When skipping an item, process the returned next item if any
            .addMatcher(rubricRefinementApi.endpoints.skipRefinementItem.matchFulfilled, (state, action) => {
                state.processingAction = false // End processing

                // Replace the item ID if a new reviewed item ID is provided
                if (action.payload?.newReviewedItemId) {
                    const currentItem = state.refinementItems.find(
                        item => item.id === action.meta.arg.originalArgs.refinementItemId,
                    )
                    if (currentItem) {
                        currentItem.id = action.payload.newReviewedItemId
                    }
                }

                // If we get a null item_to_review, the process is finished
                if (action.payload?.item === null) {
                    state.isFinished = true
                    return
                }

                // Mark the current item as skipped
                const currentItem = state.refinementItems.find(
                    item => item.id === (action.payload.newReviewedItemId || action.meta.arg.originalArgs.refinementItemId),
                )
                if (currentItem) {
                    currentItem.isSkipped = true
                    if (currentItem.type === RefinementItemTypes.SUGGESTION) {
                        currentItem.diffReviewChoice = 'rejected'
                    }
                }

                // Add the new item to the list if present in the response
                if (action.payload?.item) {
                    const newItem = action.payload.item
                    if (!state.refinementItems.some(item => item.id === newItem.id)) {
                        state.refinementItems.push(newItem)
                    }
                }

                // Update the progress
                if (action.payload?.progress) {
                    state.refinementProcess.progress = action.payload.progress
                }
            })
            // When confirming a suggestion, process the returned next item if any
            .addMatcher(rubricRefinementApi.endpoints.confirmSuggestion.matchFulfilled, (state, action) => {
                state.processingAction = false // End processing

                // Replace the item ID if a new reviewed item ID is provided
                if (action.payload?.newReviewedItemId) {
                    const currentItem = state.refinementItems.find(
                        item => item.id === action.meta.arg.originalArgs.refinementItemId,
                    )
                    if (currentItem) {
                        currentItem.id = action.payload.newReviewedItemId
                    }
                }

                // If we get a null item_to_review, the process is finished
                if (action.payload?.item === null) {
                    state.isFinished = true
                    return
                }

                // Mark the suggestion as approved
                const currentItem = state.refinementItems.find(
                    item => item.id === (action.payload.newReviewedItemId || action.meta.arg.originalArgs.refinementItemId),
                )
                if (currentItem && currentItem.type === RefinementItemTypes.SUGGESTION) {
                    currentItem.diffReviewChoice = 'approved'
                }

                // Add the new item to the list if present in the response
                if (action.payload?.item) {
                    const newItem = action.payload.item
                    if (!state.refinementItems.some(item => item.id === newItem.id)) {
                        state.refinementItems.push(newItem)
                    }
                }

                // Update the progress
                if (action.payload?.progress) {
                    state.refinementProcess.progress = action.payload.progress
                }
            })
            // reset slice on finish
            .addMatcher(rubricRefinementApi.endpoints.finishRefinement.matchFulfilled, () => {
                return initialState
            })
            // Track pending API actions
            .addMatcher(
                action =>
                    action.type.endsWith('/pending') &&
                    (action.type.includes('chooseRefinementCategory') ||
                        action.type.includes('confirmSuggestion') ||
                        action.type.includes('skipRefinementItem')),
                state => {
                    state.processingAction = true
                },
            )
    },
})

export const {
    setRefinementItems,
    setHoveredCategoryId,
    selectCategory,
    approveDiffReview,
    rejectDiffReview,
    skipItem,
    revertItemToState,
    startRefinementProcess,
    reset,
} = rubricRefinementSlice.actions

export default rubricRefinementSlice.reducer
